import { BrowserMultiFormatReader } from '@zxing/browser';
import { ChecksumException, FormatException, NotFoundException } from '@zxing/library';

export function cameraErrorMessage(error) {
  switch (error?.name) {
    case 'NotAllowedError': case 'SecurityError':
      return 'Geen cameratoegang. Sta de camera toe voor deze website in je browser en tik op Opnieuw proberen.';
    case 'NotFoundError':
      return 'Geen beschikbare camera gevonden. Je kunt de barcode ook handmatig invoeren.';
    case 'OverconstrainedError':
      return 'Deze camera is niet beschikbaar. Kies Automatische camera en probeer opnieuw.';
    case 'NotReadableError': case 'AbortError':
      return 'De camera is bezet of onderbroken. Sluit andere camera-apps en probeer opnieuw.';
    case 'InsecureCamera':
      return 'Open deze pagina via een beveiligde https-verbinding om de camera te gebruiken.';
    case 'UnsupportedCamera':
      return 'Deze browser geeft geen cameratoegang. Open de website rechtstreeks in Safari of een actuele browser.';
    case 'CameraPermissionTimeout':
      return 'Nog geen reactie van de camera. Sta cameratoegang toe en tik op Opnieuw proberen.';
    default:
      return 'De camera levert geen beeld. Tik op Opnieuw proberen om de camera opnieuw te starten.';
  }
}

// Own each stream and video node. A late old decoder can never detach a newer camera.
export function createBarcodeCameraSession({ host, deviceId, onStatus, onDevices, onResult, onError }) {
  const stopControls = controls => {
    try { Promise.resolve(controls?.stop()).catch(() => { /* optional torch teardown must not reject globally */ }); }
    catch { /* already stopped; owned tracks are stopped independently below */ }
  };
  const video = document.createElement('video');
  video.autoplay = true; video.muted = true; video.defaultMuted = true; video.playsInline = true;
  ['autoplay', 'muted', 'playsinline', 'webkit-playsinline'].forEach(name => video.setAttribute(name, ''));
  video.setAttribute('data-testid', 'barcode-scanner-video');
  video.setAttribute('aria-label', 'Live camerabeeld voor barcode');
  Object.assign(video.style, { position: 'absolute', inset: '0', width: '100%', height: '100%', objectFit: 'contain' });
  host.appendChild(video);
  const session = { cancelled: false, stream: null, controls: null, frames: false, timers: new Set(), listeners: [], frameId: null };
  const timer = (callback, ms) => { const id = setTimeout(callback, ms); session.timers.add(id); return id; };
  const listen = (node, event, callback) => { node.addEventListener(event, callback); session.listeners.push(() => node.removeEventListener(event, callback)); };
  const stop = () => {
    if (session.cancelled) return;
    session.cancelled = true;
    session.timers.forEach(clearTimeout);
    session.listeners.forEach(remove => remove());
    if (session.frameId != null) video.cancelVideoFrameCallback?.(session.frameId);
    stopControls(session.controls);
    session.stream?.getTracks().forEach(track => track.stop());
    video.pause(); video.srcObject = null; video.remove();
  };
  const fail = error => {
    if (session.cancelled) return;
    stop(); onError(cameraErrorMessage(error));
  };
  const markFrame = () => {
    if (session.cancelled || video.readyState < 2 || !video.videoWidth || !video.videoHeight || video.paused) return;
    if (!session.frames) { session.frames = true; onStatus('running'); }
  };
  const start = async () => {
    if (!window.isSecureContext) { fail({ name: 'InsecureCamera' }); return; }
    if (!navigator.mediaDevices?.getUserMedia) { fail({ name: 'UnsupportedCamera' }); return; }
    onStatus('requesting');
    const permissionTimer = timer(() => fail({ name: 'CameraPermissionTimeout' }), 15000);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: false,
        video: deviceId ? { deviceId: { exact: deviceId } } : { facingMode: { ideal: 'environment' } } });
      session.stream = stream;
      clearTimeout(permissionTimer);
      if (session.cancelled) { stream.getTracks().forEach(track => track.stop()); return; }
      onStatus('starting');
      const frameTimer = timer(() => { if (!session.frames) fail({ name: 'NoCameraFrames' }); }, 8000);
      const frameReady = () => { markFrame(); if (session.frames) clearTimeout(frameTimer); };
      ['loadeddata', 'playing', 'timeupdate'].forEach(event => listen(video, event, frameReady));
      listen(video, 'pause', () => {
        if (session.frames) timer(() => { if (video.paused) fail({ name: 'CameraPaused' }); }, 5000);
      });
      listen(video, 'error', () => fail({ name: 'CameraVideoError' }));
      stream.getVideoTracks().forEach(track => {
        listen(track, 'ended', () => fail({ name: 'CameraEnded' }));
        listen(track, 'mute', () => timer(() => { if (track.muted) fail({ name: 'CameraMuted' }); }, 5000));
      });
      // Device IDs/labels are now permission-authorized; enumeration never gates startup.
      BrowserMultiFormatReader.listVideoInputDevices().then(devices => {
        if (!session.cancelled) onDevices(devices.filter(device => !!device.deviceId));
      }).catch(() => { /* preview remains usable even when enumeration is restricted */ });
      const reader = new BrowserMultiFormatReader(undefined, { delayBetweenScanAttempts: 150, delayBetweenScanSuccess: 250, tryPlayVideoTimeout: 6000 });
      const controls = await reader.decodeFromStream(stream, video, (result, error, earlyControls) => {
        session.controls = session.controls || earlyControls;
        if (session.cancelled) { stopControls(earlyControls); return; }
        frameReady();
        if (result) onResult(result.getText(), result.getBarcodeFormat?.()?.toString?.() || '');
        else if (error && !(error instanceof NotFoundException || error instanceof ChecksumException || error instanceof FormatException)) fail(error);
      });
      if (session.cancelled) { stopControls(controls); return; }
      session.controls = controls;
      frameReady();
      if (video.requestVideoFrameCallback) session.frameId = video.requestVideoFrameCallback(frameReady);
    } catch (error) { fail(error); }
  };
  return { start, stop };
}